import {isAxiosError} from 'axios';
import {isProbs, Probs, ProbsError, probsText} from './problemDetails';

type ApiErrorResponse = {
    message?: string;
};

const parseJson = (value: string): unknown => {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

export const getApiProblem = <E extends ProbsError = ProbsError>(error: unknown): Probs<E> | null => {
    const data = isAxiosError(error) ? error.response?.data : undefined;
    const value = typeof data === 'string' ? parseJson(data) : data;
    return isProbs(value) ? (value as Probs<E>) : null;
};

const extractText = (value: unknown): string | null => {
    if (typeof value === 'string' && value.trim().length > 0) {
        const parsed = parseJson(value);
        return isProbs(parsed) ? (probsText(parsed) ?? value) : value;
    }
    if (isProbs(value)) {
        const text = probsText(value);
        if (text) {
            return text;
        }
    }
    if (value && typeof value === 'object') {
        const message = (value as ApiErrorResponse).message;
        if (typeof message === 'string' && message.trim().length > 0) {
            return message;
        }
    }
    return null;
};

export const getApiErrorMessage = (error: unknown, fallback = 'Request failed'): string => {
    if (isAxiosError(error)) {
        const responseMessage = extractText(error.response?.data);
        if (responseMessage) {
            return responseMessage;
        }
        if (typeof error.message === 'string' && error.message.trim().length > 0) {
            return error.message;
        }
        return fallback;
    }
    if (error instanceof Error && error.message.trim().length > 0) {
        return error.message;
    }
    return fallback;
};

export const notifyApiError = (error: unknown, fallback?: string) => {
    const message = getApiErrorMessage(error, fallback);
    if (typeof window !== 'undefined' && typeof window.console !== 'undefined') {
        window.console.error(message);
    }
};
